/**
 * UnifiedSearchBar — Shared Search + Filter UI
 * ──────────────────────────────────────────────
 * Identical appearance on every page. Reads directly from PlatformContext.
 * No props needed for search/filter state — it's all in context.
 *
 * Features:
 *   - Search input with instant results dropdown
 *   - Filter toggle button with active count badge
 *   - Active filter pills bar (zone, type, sort, quick) with X to remove
 *   - "Clear all" button when any filters are active
 *   - Adapts to page context via `variant` prop:
 *       'map'     → absolute positioned, floats over map (transparent bg)
 *       'page'    → sticky top, solid bg, sits inside page flow
 *
 * Collections have been consolidated into the SelectionTray (bottom workspace).
 * They no longer appear in this search bar or its filter panel.
 *
 * Props (all optional — context provides the data):
 *   variant      — 'map' | 'page' (default 'page')
 *   onResultClick — called when user clicks a search result (receives project)
 *   className    — extra classes for the outer wrapper
 *   showFilterPanel — if true, renders the full filter panel inline
 *                     (for pages that want it; map has its own panel)
 *
 * Place at: src/components/UnifiedSearchBar.jsx
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Search, X, SlidersHorizontal, Clock, GitFork, Eye, Star,
  User, Target, Package, Monitor, Layers, Sparkles,
  Palette, Music, Utensils, BookOpen, Stethoscope, Cpu, TrendingUp,
  Users, ChevronDown,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { usePlatform } from '../context/PlatformContext';
import { useTray } from '../context/TrayContext';
import { ZONES, PROJECT_TYPES, getProjectZone } from '../config/zones';

// ─────────────────────────────────────────────────────────────────────────────
// ZONE ICONS (shared constant)
// ─────────────────────────────────────────────────────────────────────────────

const ZONE_ICONS = {
  build: Package, beautiful: Palette, sound: Music, taste: Utensils,
  story: BookOpen, heal: Stethoscope, smart: Cpu, sell: TrendingUp,
};

const SORT_LABELS = {
  newest: 'Newest', most_forked: 'Most Forked', most_viewed: 'Most Viewed',
  most_starred: 'Most Starred', surprising: 'Surprising',
};

const QUICK_FILTER_LABELS = {
  starred: 'My Stars', mine: 'My Projects',
  challenges: 'Has Challenges', has_forks: 'Has Forks',
};

const TYPE_LABELS = {
  physical: 'Physical', digital: 'Digital', hybrid: 'Hybrid',
};

// ─────────────────────────────────────────────────────────────────────────────
// FILTER PILL — small removable tag
// ─────────────────────────────────────────────────────────────────────────────

const FilterPill = ({ label, color, icon: Icon, onRemove }) => (
  <span
    className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-lg text-[11px] font-medium border transition-all group"
    style={{
      backgroundColor: color ? `${color}12` : 'rgba(63,63,70,0.3)',
      borderColor: color ? `${color}25` : 'rgba(63,63,70,0.5)',
      color: color || '#a1a1aa',
    }}
  >
    {Icon && <Icon className="w-3 h-3 flex-shrink-0" />}
    <span className="truncate max-w-[80px]">{label}</span>
    <button
      onClick={(e) => { e.stopPropagation(); onRemove(); }}
      className="p-0.5 rounded hover:bg-white/10 transition-colors ml-0.5 flex-shrink-0"
    >
      <X className="w-2.5 h-2.5" />
    </button>
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVE FILTER PILLS BAR
// ─────────────────────────────────────────────────────────────────────────────

const ActiveFilterPillsBar = ({ filters }) => {
  const {
    activeZones, activeQuickFilters, activeTypeFilters, sortBy,
    toggleZone, toggleQuickFilter, toggleTypeFilter, setSortBy,
    clearAllFilters, activeFilterCount,
  } = filters;

  if (activeFilterCount === 0) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {/* Zone pills */}
      {activeZones.map(zoneId => {
        const zone = ZONES[zoneId];
        if (!zone) return null;
        const Icon = ZONE_ICONS[zoneId];
        return (
          <FilterPill
            key={`z-${zoneId}`}
            label={zone.shortLabel}
            color={zone.color}
            icon={Icon}
            onRemove={() => toggleZone(zoneId)}
          />
        );
      })}

      {/* Type pills */}
      {activeTypeFilters.map(typeId => {
        const cfg = PROJECT_TYPES[typeId];
        if (!cfg) return null;
        return (
          <FilterPill
            key={`t-${typeId}`}
            label={TYPE_LABELS[typeId] || typeId}
            color={cfg.color}
            onRemove={() => toggleTypeFilter(typeId)}
          />
        );
      })}

      {/* Quick filter pills */}
      {activeQuickFilters.map(filterId => (
        <FilterPill
          key={`q-${filterId}`}
          label={QUICK_FILTER_LABELS[filterId] || filterId}
          onRemove={() => toggleQuickFilter(filterId)}
        />
      ))}

      {/* Sort pill (only if not default) */}
      {sortBy !== 'newest' && (
        <FilterPill
          key="sort"
          label={SORT_LABELS[sortBy] || sortBy}
          icon={Clock}
          onRemove={() => setSortBy('newest')}
        />
      )}

      {/* Clear all — always visible when any filter is active */}
      {activeFilterCount >= 1 && (
        <button
          onClick={() => { clearAllFilters(); }}
          className="text-[11px] text-red-400 hover:text-red-300 font-medium transition-colors px-2.5 py-1 rounded-lg hover:bg-red-500/10 border border-transparent hover:border-red-500/20"
        >
          Clear all
        </button>
      )}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// COMPACT FILTER PANEL — zone chips + sort + type
// Rendered when the filter toggle is open on non-map pages.
// ─────────────────────────────────────────────────────────────────────────────

const CompactFilterPanel = ({ filters, onClose }) => {
  const {
    activeZones, toggleZone,
    activeTypeFilters, toggleTypeFilter,
    activeQuickFilters, toggleQuickFilter,
    activeShapeFilters = [], toggleShapeFilter,
    sortBy, setSortBy,
    clearAllFilters, activeFilterCount,
  } = filters;

  const { allProjects } = usePlatform();
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortRef = useRef(null);

  const SORT_OPTIONS = [
    { id: 'newest', label: 'Newest First', icon: Clock },
    { id: 'surprising', label: 'Surprising', icon: Sparkles },
    { id: 'most_forked', label: 'Most Forked', icon: GitFork },
    { id: 'most_viewed', label: 'Most Viewed', icon: Eye },
    { id: 'most_starred', label: 'Most Starred', icon: Star },
  ];

  const QUICK_FILTERS = [
    { id: 'starred', label: 'My Stars', icon: Star, color: 'amber' },
    { id: 'mine', label: 'My Projects', icon: User, color: 'purple' },
    { id: 'challenges', label: 'Has Challenges', icon: Target, color: 'orange' },
    { id: 'has_forks', label: 'Has Forks', icon: GitFork, color: 'emerald' },
  ];

  const TYPE_FILTERS = [
    { id: 'physical', label: 'Physical', icon: Package, color: PROJECT_TYPES.physical.color },
    { id: 'digital', label: 'Digital', icon: Monitor, color: PROJECT_TYPES.digital.color },
    { id: 'hybrid', label: 'Hybrid', icon: Layers, color: PROJECT_TYPES.hybrid.color },
  ];

  const SHAPE_FILTERS = [
    { id: 'round',         symbol: '●', label: 'Round' },
    { id: 'wide',          symbol: '▬', label: 'Wide' },
    { id: 'narrow',        symbol: '▲', label: 'Narrow' },
    { id: 'twisted',       symbol: '∿', label: 'Twisted' },
    { id: 'symmetrical',   symbol: '⬡', label: 'Symmetrical' },
    { id: 'asymmetrical',  symbol: '⬠', label: 'Asymmetrical' },
    { id: 'rising',        symbol: '↑', label: 'Rising' },
    { id: 'sinking',       symbol: '↓', label: 'Sinking' },
    { id: 'expanding',     symbol: '◎', label: 'Expanding' },
    { id: 'contracting',   symbol: '⊙', label: 'Contracting' },
  ];

  const QUICK_COLOR_MAP = {
    amber:   'text-amber-400 bg-amber-500/15 border-amber-500/40',
    purple:  'text-purple-400 bg-purple-500/15 border-purple-500/40',
    orange:  'text-orange-400 bg-orange-500/15 border-orange-500/40',
    emerald: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/40',
  };

  // Zone project counts
  const zoneProjectCounts = useMemo(() => {
    const counts = {};
    (allProjects || []).forEach(p => {
      try {
        const zone = getProjectZone(p);
        if (zone) counts[zone.id] = (counts[zone.id] || 0) + 1;
      } catch {}
    });
    return counts;
  }, [allProjects]);

  const panelRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          !e.target.closest('[data-filter-toggle]')) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-80 bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-xl shadow-2xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto"
      style={{ animation: 'dropdownIn 0.15s ease-out' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 sticky top-0 bg-zinc-900/95 backdrop-blur-xl z-10">
        <h3 className="text-sm font-semibold text-white">Filters & Sort</h3>
        <div className="flex items-center gap-2">
          {activeFilterCount > 0 && (
            <button onClick={() => { clearAllFilters(); }} className="text-[11px] text-purple-400 hover:text-purple-300 transition-colors">
              Clear all
            </button>
          )}
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded-lg transition-colors">
            <X className="w-3.5 h-3.5 text-zinc-500" />
          </button>
        </div>
      </div>

      {/* ── Quick Filters ── */}
      <div className="px-4 pb-3">
        <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2">Quick Filters</label>
        <div className="flex flex-wrap gap-1.5">
          {QUICK_FILTERS.map(filter => {
            const isActive = activeQuickFilters.includes(filter.id);
            const Icon = filter.icon;
            return (
              <button
                key={filter.id}
                onClick={() => toggleQuickFilter(filter.id)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all',
                  isActive
                    ? QUICK_COLOR_MAP[filter.color]
                    : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                )}
              >
                <Icon className="w-3 h-3" />
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="h-px bg-zinc-800 mx-4" />

      {/* ── Project Type ── */}
      <div className="px-4 py-3">
        <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2">Project Type</label>
        <div className="flex gap-1.5">
          {TYPE_FILTERS.map(tf => {
            const isActive = activeTypeFilters.includes(tf.id);
            const Icon = tf.icon;
            return (
              <button
                key={tf.id}
                onClick={() => toggleTypeFilter(tf.id)}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all',
                  isActive
                    ? ''
                    : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-300'
                )}
                style={isActive ? {
                  color: tf.color,
                  backgroundColor: tf.color + '18',
                  borderColor: tf.color + '60',
                } : {}}
              >
                <Icon className="w-3 h-3" />
                {tf.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="h-px bg-zinc-800 mx-4" />

      {/* ── Shape / Feel ── */}
      <div className="px-4 py-3">
        <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2">
          How does it feel?
        </label>
        <div className="grid grid-cols-5 gap-1.5">
          {SHAPE_FILTERS.map(shape => {
            const isActive = (activeShapeFilters || []).includes(shape.id);
            return (
              <button
                key={shape.id}
                onClick={() => toggleShapeFilter?.(shape.id)}
                title={shape.label}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 py-2 rounded-lg border transition-all',
                  isActive
                    ? 'bg-violet-500/20 border-violet-500/50 text-violet-300'
                    : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200'
                )}
              >
                <span className={cn('text-base leading-none', isActive ? 'text-violet-300' : 'text-zinc-300')}>
                  {shape.symbol}
                </span>
                <span className="text-[9px] font-medium leading-none">{shape.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="h-px bg-zinc-800 mx-4" />

      {/* ── Zones with counts ── */}
      <div className="px-4 py-3">
        <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2">Zones</label>
        <div className="grid grid-cols-2 gap-1.5">
          {Object.entries(ZONES).map(([id, zone]) => {
            const count = zoneProjectCounts[id] || 0;
            const isActive = activeZones.includes(id);
            return (
              <button
                key={id}
                onClick={() => toggleZone(id)}
                className={cn(
                  'flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs transition-all text-left',
                  isActive
                    ? 'border'
                    : 'bg-zinc-800/30 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60 border border-transparent'
                )}
                style={isActive ? {
                  backgroundColor: `${zone.color}12`,
                  borderColor: `${zone.color}40`,
                  color: zone.color,
                } : {}}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: zone.color }} />
                <span className="truncate flex-1">{zone.shortLabel}</span>
                <span className={cn(
                  'text-[10px] font-medium flex-shrink-0',
                  isActive ? '' : 'text-zinc-600'
                )} style={isActive ? { color: zone.color } : {}}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="h-px bg-zinc-800 mx-4" />

      {/* ── Sort ── */}
      <div className="px-4 py-3 pb-4">
        <label className="block text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-2">Sort by</label>
        <div className="relative" ref={sortRef}>
          <button
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            className="w-full flex items-center justify-between px-3 py-2 bg-zinc-800/40 border border-zinc-700/50 rounded-lg text-sm text-zinc-300 hover:border-zinc-600 transition-colors"
          >
            <span className="flex items-center gap-2">
              {(() => {
                const current = SORT_OPTIONS.find(s => s.id === sortBy);
                const SIcon = current?.icon || Clock;
                return (<><SIcon className="w-3.5 h-3.5 text-zinc-500" />{current?.label || 'Newest First'}</>);
              })()}
            </span>
            <ChevronDown className={cn('w-3.5 h-3.5 text-zinc-500 transition-transform', showSortDropdown && 'rotate-180')} />
          </button>
          {showSortDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg z-10 overflow-hidden">
              {SORT_OPTIONS.map(option => {
                const SIcon = option.icon;
                return (
                  <button
                    key={option.id}
                    onClick={() => { setSortBy(option.id); setShowSortDropdown(false); }}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                      sortBy === option.id ? 'text-purple-400 bg-purple-500/10' : 'text-zinc-300 hover:bg-zinc-700'
                    )}
                  >
                    <SIcon className="w-3.5 h-3.5" />
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const UnifiedSearchBar = ({
  variant = 'page',
  onResultClick,
  onChallengeClick,
  onUserClick,
  className,
  showFilterPanel = true,
}) => {
  const { search, filters, allProjects } = usePlatform();
  const totalFilterCount = filters.activeFilterCount;
  const [showResults, setShowResults] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Show results when typing
  useEffect(() => {
    if (search.debouncedQuery && search.debouncedQuery.trim().length >= 2) {
      setShowResults(true);
    }
  }, [search.debouncedQuery]);

  const handleClear = useCallback(() => {
    search.clear();
    setShowResults(false);
    inputRef.current?.focus();
  }, [search]);

  const handleResultClick = useCallback((project) => {
    setShowResults(false);
    onResultClick?.(project);
  }, [onResultClick]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setShowResults(false);
      e.target.blur();
    }
  }, []);

  // Get zone info for search results
  const getZone = useCallback((project) => {
    try { return getProjectZone(project); } catch { return null; }
  }, []);

  const isMapVariant = variant === 'map';

  // Wrapper classes differ by variant
  const wrapperCn = isMapVariant
    ? 'absolute top-16 left-4 right-4 z-20 flex items-center gap-2 pointer-events-none'
    : cn('sticky top-0 z-30 bg-zinc-950/90 backdrop-blur-xl pt-3 pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6', className);

  return (
    <div className={wrapperCn}>
      {/* ── Search input ── */}
      <div
        className={cn('relative flex-1', isMapVariant ? 'max-w-md pointer-events-auto' : 'max-w-full')}
        ref={searchRef}
      >
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 z-10" />
        <input
          ref={inputRef}
          type="text"
          value={search.query}
          onChange={(e) => search.setQuery(e.target.value)}
          onFocus={() => { if (search.debouncedQuery?.trim()) setShowResults(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Search projects, tags, people..."
          className={cn(
            'w-full bg-zinc-900/90 backdrop-blur-xl border rounded-xl pl-10 pr-10 py-2.5',
            'text-sm text-white placeholder:text-zinc-500',
            'focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50',
            'transition-all',
            showResults && search.results.length > 0
              ? 'border-zinc-700 rounded-b-none'
              : 'border-zinc-800',
          )}
        />

        {/* Clear button */}
        {search.query && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-700 transition-colors"
          >
            <X className="w-3.5 h-3.5 text-zinc-500" />
          </button>
        )}

        {/* Search results dropdown */}
        {showResults && search.debouncedQuery?.trim() && (
          <div
            className="absolute top-full left-0 right-0 bg-zinc-900/95 backdrop-blur-xl border border-t-0 border-zinc-700 rounded-b-xl shadow-2xl overflow-hidden z-30"
            style={{ animation: 'dropdownIn 0.15s ease-out' }}
          >
            {search.loading ? (
              <div className="px-4 py-6 text-center">
                <div className="w-5 h-5 border-2 border-zinc-700 border-t-purple-500 rounded-full animate-spin mx-auto" />
              </div>
            ) : (search.results.length > 0 || (search.challengeResults?.length > 0) || (search.userResults?.length > 0)) ? (
              <div className="max-h-96 overflow-y-auto">
                {/* ── Projects ── */}
                {search.results.length > 0 && (
                  <>
                    <div className="px-3 pt-2 pb-1">
                      <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Projects</span>
                    </div>
                    {search.results.slice(0, 6).map(project => {
                      const zone = getZone(project);
                      const img = project.image_url || project.imageUrl;
                      return (
                        <div
                          key={`p-${project.id}`}
                          onClick={() => handleResultClick(project)}
                          className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors hover:bg-zinc-800/60"
                        >
                          <div className="w-8 h-8 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0 border border-zinc-700/50">
                            {img ? (
                              <img src={img} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div
                                className="w-full h-full flex items-center justify-center text-xs font-semibold"
                                style={zone ? { background: `${zone.color}15`, color: zone.color } : { color: '#71717a' }}
                              >
                                {project.name?.[0]?.toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {zone && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: zone.color }} />}
                              <span className="text-sm font-medium text-white truncate">{project.name}</span>
                            </div>
                            <p className="text-[11px] text-zinc-500 truncate">
                              {project.author?.username ? `@${project.author.username}` : ''}
                              {zone ? ` · ${zone.shortLabel}` : ''}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                {/* ── Challenges ── */}
                {search.challengeResults?.length > 0 && (
                  <>
                    <div className="px-3 pt-2.5 pb-1 border-t border-zinc-800/60">
                      <span className="text-[10px] font-semibold text-amber-400/70 uppercase tracking-wider">Challenges</span>
                    </div>
                    {search.challengeResults.slice(0, 3).map(challenge => {
                      const title = challenge.challenge_title || challenge.challengeTitle || challenge.title || challenge.prompt || 'Untitled';
                      const responses = challenge.response_count || challenge.responseCount || 0;
                      const isOpen = responses === 0;
                      return (
                        <div
                          key={`c-${challenge.id}`}
                          onClick={() => { setShowResults(false); onChallengeClick?.(challenge); }}
                          className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors hover:bg-zinc-800/60"
                        >
                          <div className={cn(
                            'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border',
                            isOpen ? 'bg-amber-500/10 border-amber-500/25' : 'bg-zinc-800 border-zinc-700/50',
                          )}>
                            <Target className={cn('w-4 h-4', isOpen ? 'text-amber-400' : 'text-zinc-500')} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-white truncate block">{title}</span>
                            <p className="text-[11px] text-zinc-500">
                              {isOpen ? (
                                <span className="text-amber-400">Unanswered</span>
                              ) : (
                                `${responses} response${responses !== 1 ? 's' : ''}`
                              )}
                              {challenge.project?.name ? ` · on ${challenge.project.name}` : ''}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                {/* ── Users ── */}
                {search.userResults?.length > 0 && (
                  <>
                    <div className="px-3 pt-2.5 pb-1 border-t border-zinc-800/60">
                      <span className="text-[10px] font-semibold text-cyan-400/70 uppercase tracking-wider">People</span>
                    </div>
                    {search.userResults.slice(0, 3).map(user => {
                      const avatar = user.profile_picture_url || user.profilePictureUrl;
                      const uname = user.username || user.name;
                      return (
                        <div
                          key={`u-${user.id}`}
                          onClick={() => { setShowResults(false); onUserClick?.(user); }}
                          className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors hover:bg-zinc-800/60"
                        >
                          <div className="w-8 h-8 rounded-full bg-zinc-800 overflow-hidden flex-shrink-0 border border-zinc-700/50">
                            {avatar ? (
                              <img src={avatar} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Users className="w-4 h-4 text-zinc-500" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-white truncate block">@{uname}</span>
                            {user.bio && <p className="text-[11px] text-zinc-500 truncate">{user.bio}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Footer count */}
                <div className="px-3 py-2 border-t border-zinc-800 bg-zinc-900/80">
                  <p className="text-[11px] text-zinc-600">
                    {search.results.length} project{search.results.length !== 1 ? 's' : ''}
                    {(search.challengeResults?.length || 0) > 0 && ` · ${search.challengeResults.length} challenge${search.challengeResults.length !== 1 ? 's' : ''}`}
                    {(search.userResults?.length || 0) > 0 && ` · ${search.userResults.length} ${search.userResults.length !== 1 ? 'people' : 'person'}`}
                  </p>
                </div>
              </div>
            ) : (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-zinc-500">No results for &ldquo;{search.debouncedQuery}&rdquo;</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Filter toggle button ── */}
      {showFilterPanel && (
        <div className={cn('relative', isMapVariant && 'pointer-events-auto')}>
          <button
            data-filter-toggle
            onClick={() => setShowFilters(!showFilters)}
            className={cn(
              'flex items-center gap-2 px-3.5 py-2.5 bg-zinc-900/90 backdrop-blur-xl border rounded-xl transition-all',
              showFilters
                ? 'border-purple-500 text-purple-400'
                : 'border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700',
            )}
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Filters</span>
            {totalFilterCount > 0 && (
              <span className="w-5 h-5 rounded-full bg-purple-500 text-white text-[10px] font-bold flex items-center justify-center">
                {totalFilterCount}
              </span>
            )}
          </button>

          {/* Filter panel dropdown */}
          {showFilters && (
            <CompactFilterPanel
              filters={filters}
              onClose={() => setShowFilters(false)}
            />
          )}
        </div>
      )}

      {/* ── Active filter pills (below search bar) ── */}
      {!isMapVariant && totalFilterCount > 0 && (
        <div className="w-full mt-2 px-2.5 py-2 bg-zinc-900/60 border border-zinc-800/50 rounded-xl">
          <ActiveFilterPillsBar filters={filters} />
        </div>
      )}

      <style>{`
        @keyframes dropdownIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default UnifiedSearchBar;